import { memo, useEffect, useMemo, useState } from 'react'
import type { FC, Dispatch, SetStateAction } from 'react'
import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import type { TypeProductCard } from '@/schema'
import { callAPI } from '@/api'
import { getFilters, getFilteredProducts, getFeaturedFilters, generateQueryParams, getFiltersFromQuery } from '@dy/commons/utils'
import { useMount, useScrollViewer } from '@dy/commons/hooks'
import { createGrid, getRelative, mq } from '@/styles'
import { Layout, HeadTag } from '@/components'
import { HeaderMarquee } from '@dy/commons/components'
import { callLocales } from '@/i18n'
import { SectionCategories, SectionFilters, SectionFiltersFeatured, SectionProductList, SectionProductDetail, TypeProductDetailVariant } from '../../components/shop'

const SEO = {
  title: 'Hellomamma | Shop',
  description: 'Shop page'
}

type TypeShopPage = {
  data: any,
  error?: any,
  layout: any
}

type productsType = { all: TypeProductCard[], current: TypeProductCard[] }
type currentProductsType = [
  productsType,
  Dispatch<SetStateAction<productsType>>
]

export type productDetailType = [
  TypeProductDetailVariant,
  Dispatch<SetStateAction<TypeProductDetailVariant>>
]

function categoriesPaths(categories, locales) {
  var paths = []
  for(const locale of locales) {
    for(const category of categories) {
      if(category.slug[locale]) paths = categoryPath(category, locale, paths)
    }
  }
  return paths
}

function categoryPath(category, locale, paths) {
  const params = { slug: category.slug[locale].split('/') }
  const path = { params, locale }

  paths.push(path)

  for(const subcategory of category.subcategories) {
    if(subcategory.slug[locale]) paths = categoryPath(subcategory, locale, paths)
  }

  return paths
}

export async function getStaticPaths({ locales }) {
  const [data, error] = await callAPI({ type: 'categories' })
  const paths = !error ? categoriesPaths(data, locales) : []

  return { paths, fallback: 'blocking' }
}

const NEXT_LOCALE = process.env.NEXT_PUBLIC_LOCALE
export async function getStaticProps({ params, locale }) {
  const { slug } = params
  // console.log('🟡 slug', slug)
  const lang = locale ?? NEXT_LOCALE
  const [layoutData, layoutError] = await callAPI({ type: 'layout', params: { locale: lang } })
  const [data, error] = await callAPI({ type: 'category', params: { slug: slug.join('/'), locale: lang } })


  // console.log('layoutData', layoutData)
  // console.log('layoutError', layoutError)
  // console.log('🟣 data', data)
  // console.log('🟣🟡 error', error)

  if(!error && layoutError) throw new Error('Error fetching layout data')
  if(error) return { notFound: true }

  const [locales, layoutLocale] = await callLocales({ type: 'home', locale: lang })


  return {
    props: {
      locales,
      layoutLocale,
      // params,
      data,
      layout: layoutData
    },
    revalidate: 600
  }
}

let firstLoad = true
const ShopPage: NextPage<TypeShopPage> & { Layout?:FC } = memo(({ data = { products: [] }, layout }) => {
  const { locale, defaultLocale, asPath, query, push } = useRouter() || { locale: '', defaultLocale: '', asPath: '', query: {}, push: () => {} }

  // if(error?.status) return <ErrorPage error={error} />
  // if(!data?.slug) return null
  const isMount = useMount()
  const { seo, slug, categories, products } = data

  const [productDetailVisible, setProductDetailVisible] = useState(false)
  const [productDetailed, setProductDetailed]:productDetailType = useState(null)
  const [showLargeProductDetail, setShowLargeProductDetail] = useState(false)
  const [selectedCard, setSelectedCard] = useState(null)
  const [activeFilters, setActiveFilters] = useState({ sort: {recommended: true} })
  const [featuredFilters, setFeaturedFilters] = useState([])
  const filters = useMemo(() => getFilters(products, locale), [products])
  const [currentProducts, setCurrentProducts]:currentProductsType = useState({
    all: Array.from(products),
    current: Array.from(products)
  })

  const scrollState = useScrollViewer(160)
  const isScrolling = useMemo(() => scrollState !== 'TOP', [scrollState])

  const displayProductDetail = () => {
    if(!productDetailVisible) setProductDetailVisible(true)
  }

  const hideProductDetail = () => {
    if(productDetailVisible) {
      setProductDetailVisible(false)
      setSelectedCard(null)
    }
    if(typeof window === 'object') {
      window.history.back()
    }
  }

  const updateQueryParams = ({ query, activeFilters }) => {
    const slug = typeof query.slug === 'string' ? query.slug : query.slug.join('/')
    // const { queryParams, stringParams} = generateQueryParams(activeFilters)
    const { stringParams} = generateQueryParams(activeFilters)
    push('/shop/[...slug]', `/shop/${slug}?${stringParams}`, { shallow: true })
  }

  useEffect(() => {
    if (window.history.scrollRestoration !== 'manual') {
      window.history.scrollRestoration = 'manual'
    }

    return () => {
      window.history.scrollRestoration = 'auto'
    }
  }, [])

  useEffect(() => {
    // @ts-ignore
    if(!firstLoad && query?.slug) {
      setCurrentProducts({
        all: products,
        current: getFilteredProducts(products, activeFilters)
      })

      setFeaturedFilters(getFeaturedFilters(activeFilters, filters?.[0]))
    }
    // @ts-ignore
  }, [isMount, query.slug])
  // Note for this useEffect, initially was [isMount, activeFilters] but
  // since when we update the query params in the next useEffect, this cause a query.slug update
  // (even if the slug remains the same)

  useEffect(() => {
    if(!isMount) return

    if(firstLoad) { firstLoad = false }
    else {
      updateQueryParams({ query, activeFilters })
    }
  }, [isMount, activeFilters])

  // Get param filters from URL
  useEffect(() => {
    if(filters) {
      const incomingActiveFiltersFromUrl = getFiltersFromQuery(asPath, filters[1])
      //@ts-ignore
      setActiveFilters(incomingActiveFiltersFromUrl)
    }
  }, [filters])

  if(!data) return <></>

  return (
    <>
      {data && <HeadTag data={seo ?? SEO} jsonld={{ data, pageType: 'products' }} pathname='shop/' slug={slug} defaultLocale={defaultLocale} />}
      <HeaderMarquee text={categories?.filter((category) => asPath.includes(category.slug[locale]))[0]?.name} />
      <Main>
        <span id='main-top'></span>
        <SectionCategories categories={categories ?? []} productDetailVisible={productDetailVisible} displayPacks={layout?.header?.packs}/>
        <SectionFilters productDetailVisible={productDetailVisible} isScrolling={isScrolling} filters={filters?.[0]} activeFilters={activeFilters} setActiveFilters={setActiveFilters} resultsLength={currentProducts?.current?.length} />
        <SectionFiltersFeatured filters={featuredFilters} productDetailVisible={productDetailVisible} />
        <SectionProductList key={`list-products-${asPath}`} products={currentProducts.current} productDetailVisible={productDetailVisible} displayProductDetail={displayProductDetail} setProductDetailed={setProductDetailed} selectedCard={selectedCard} setSelectedCard={setSelectedCard} setShowLargeProductDetail={setShowLargeProductDetail} />
        <SectionProductDetail className='plp-klk' productDetailVisible={productDetailVisible} hideProductDetail={hideProductDetail} variant={productDetailed} setSelectedCard={setSelectedCard} filters={filters?.[0]} showLargeProductDetail={showLargeProductDetail} />
      </Main>
    </>
  )
})

ShopPage.Layout = Layout
export default ShopPage


const Main = styled.main`
  ${createGrid()}
  padding: 0;
  position: relative;

  #main-top {
    background-color: ${({ theme }) => theme.colors.background};
    display: block;
    height: 2px;
    opacity: .01;
    position: absolute;
    top: 0;
    user-select: none;
    width: 100%;

    ${mq.greaterThan('tablet')`
      transform: translateY(-${getRelative(20, 'desktop')});
    `}
  }
`
